import React, { useState, useEffect, useMemo } from "react";
/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
  LabelList,
} from 'recharts';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  FormControlLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Collapse,
  RadioGroup,
  Radio,
  FormLabel,
  Stack,
  Checkbox,
  Alert,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import Grid from '@mui/material/Grid';
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';
import { formatTime12Hour } from '../utils';
import { useEmployees, useLocations } from "../hooks/useFirestoreData";
import useAssignmentsForLocation from '../hooks/useAssignmentsForLocation';
import LocationSelector from "./LocationSelector";

const SURFACE_STYLES = {
  borderRadius: 4,
  border: '1px solid rgba(148, 163, 184, 0.2)',
  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
};

function formatEmployeeName(employee) {
  if (!employee) return 'Unknown Employee';
  return employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown Employee';
}

function formatScheduleEntry(dayAvail, reasons) {
  if (!dayAvail) return { tone: 'neutral', summary: 'No entry', detail: '' };

  if (dayAvail.type === 'all_day_available') {
    return { tone: 'positive', summary: 'Available all day', detail: 'Open for any shift' };
  }

  if (dayAvail.type === 'all_day_not_available') {
    return { tone: 'negative', summary: 'Unavailable all day', detail: reasons.find(r => r.value === dayAvail.reason)?.label || dayAvail.reason || 'Not available' };
  }

  const start = formatTime12Hour(dayAvail.startTime || '09:00');
  const end = formatTime12Hour(dayAvail.endTime || '17:00');

  if (dayAvail.type === 'available') {
    return {
      tone: 'positive',
      summary: `Available ${start} - ${end}`,
      detail: dayAvail.comments || 'Limited availability window',
    };
  }

  if (dayAvail.type === 'not_available') {
    return {
      tone: 'negative',
      summary: `Unavailable ${start} - ${end}`,
      detail: reasons.find(r => r.value === dayAvail.reason)?.label || dayAvail.reason || dayAvail.comments || 'Blocked time window',
    };
  }

  return { tone: 'neutral', summary: 'No entry', detail: '' };
}


export default function EmployeeAvailabilityManager() {
  const [selectedLocation, setSelectedLocation] = useState("");
  const locations = useLocations(selectedLocation, setSelectedLocation);
  const employees = useEmployees();
  
  // Listen for per-location assignments map (employeeId -> per-location nested slice)
  const { map: assignmentsMap, loading: assignmentsLoading, error: assignmentsError } = useAssignmentsForLocation(selectedLocation);
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null); // Track record being edited
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [expandedRecords, setExpandedRecords] = useState({});
  const [availabilityType, setAvailabilityType] = useState("recurring");
  const [startDate, setStartDate] = useState(getTodayString());
  const [endDate, setEndDate] = useState("");
  const [weeklyAvailability, setWeeklyAvailability] = useState({});
  const [existingRecurring, setExistingRecurring] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Existing availability data
  const [availabilityRecords, setAvailabilityRecords] = useState([]);

  // Dashboard state (removed unused vars)

  // (Metrics and charts removed per user request)

  // --- Local helpers / constants (kept minimal) ---
  function getTodayString() {
    return new Date().toISOString().split('T')[0];
  }

  const DAYS_OF_WEEK = [
    { key: 1, label: 'Monday', fullLabel: 'Monday', color: '#60a5fa' },
    { key: 2, label: 'Tuesday', fullLabel: 'Tuesday', color: '#34d399' },
    { key: 3, label: 'Wednesday', fullLabel: 'Wednesday', color: '#f97316' },
    { key: 4, label: 'Thursday', fullLabel: 'Thursday', color: '#f43f5e' },
    { key: 5, label: 'Friday', fullLabel: 'Friday', color: '#a78bfa' },
    { key: 6, label: 'Saturday', fullLabel: 'Saturday', color: '#f59e0b' },
    { key: 0, label: 'Sunday', fullLabel: 'Sunday', color: '#94a3b8' },
  ];

  // Helper: interpolate color between red (weak) and green (strong)
  const valueToColor = (value, max) => {
    const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
    // red -> green
    const r1 = 239, g1 = 68, b1 = 68; // #ef4444
    const r2 = 22, g2 = 163, b2 = 74; // #16a34a
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // availability chart data is computed later (after filteredEmployees is declared)

  const AVAILABILITY_TYPES = [
    { value: 'recurring', label: 'Recurring' },
    { value: 'temporary', label: 'Temporary' },
  ];

  const UNAVAILABLE_REASONS = [
    { value: 'sick', label: 'Sick' },
    { value: 'vacation', label: 'Vacation' },
    { value: 'other', label: 'Other' },
  ];

  function DayAvailabilityRow({ day, availability, onChange }) {
    // NOTE: schedulingHoursDay prop will be passed when available to indicate open/close and start/end bounds
    // Accept schedulingHoursDay via availability._scheduling (set by initializer)
    const schedulingHoursDay = availability && availability._scheduling ? availability._scheduling : null;
    const isClosed = schedulingHoursDay ? schedulingHoursDay.open === false : false;

    const handleType = (e) => onChange(day.key, { ...availability, type: e.target.value });
    const handleStart = (e) => onChange(day.key, { ...availability, startTime: e.target.value });
    const handleEnd = (e) => onChange(day.key, { ...availability, endTime: e.target.value });
    const handleReason = (e) => onChange(day.key, { ...availability, reason: e.target.value });

    const minTime = schedulingHoursDay && schedulingHoursDay.start ? schedulingHoursDay.start : undefined;
    const maxTime = schedulingHoursDay && schedulingHoursDay.end ? schedulingHoursDay.end : undefined;
    const isTimedMode = availability.type === 'available' || availability.type === 'not_available';
    const isUnavailableMode = availability.type === 'all_day_not_available' || availability.type === 'not_available';
    const storeHoursLabel = isClosed
      ? 'Location closed'
      : (minTime && maxTime ? `Store hours ${formatTime12Hour(minTime)} - ${formatTime12Hour(maxTime)}` : 'No store hours set');
    const statusLabel = availability.type === 'all_day_available'
      ? 'Open all day'
      : availability.type === 'all_day_not_available'
        ? 'Unavailable all day'
        : availability.type === 'available'
          ? 'Limited availability'
          : 'Blocked hours';
    const toneStyles = isClosed
      ? {
          bg: alpha('#64748b', 0.08),
          border: alpha('#64748b', 0.22),
          badgeBg: alpha('#64748b', 0.12),
          badgeColor: '#475569',
        }
      : isUnavailableMode
        ? {
            bg: alpha('#ef4444', 0.06),
            border: alpha('#ef4444', 0.2),
            badgeBg: alpha('#ef4444', 0.12),
            badgeColor: '#dc2626',
          }
        : {
            bg: alpha('#22c55e', 0.06),
            border: alpha('#22c55e', 0.18),
            badgeBg: alpha('#16a34a', 0.12),
            badgeColor: '#15803d',
          };

    return (
      <Box
        key={day.key}
        sx={{
          p: 1.4,
          borderRadius: 3,
          border: `1px solid ${toneStyles.border}`,
          bgcolor: toneStyles.bg,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: day.color,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>
                {day.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15 }}>
                {storeHoursLabel}
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              alignSelf: { xs: 'flex-start', md: 'center' },
              px: 1,
              py: 0.4,
              borderRadius: 999,
              bgcolor: toneStyles.badgeBg,
              color: toneStyles.badgeColor,
              fontWeight: 800,
              fontSize: '0.78rem',
            }}
          >
            {statusLabel}
          </Box>
        </Stack>

        <Box
          sx={{
            mt: 1.25,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: isTimedMode ? 'minmax(0, 1.45fr) repeat(2, minmax(120px, 0.7fr)) minmax(0, 1fr)' : 'minmax(0, 1.6fr) minmax(0, 1fr)' },
            gap: 1,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={availability.type} onChange={handleType} label="Status" disabled={isClosed}>
              <MenuItem value="all_day_available">All day available</MenuItem>
              <MenuItem value="all_day_not_available">All day unavailable</MenuItem>
              <MenuItem value="available">Available during these hours</MenuItem>
              <MenuItem value="not_available">Unavailable during these hours</MenuItem>
            </Select>
          </FormControl>

          {isTimedMode && (
            <TextField
              type="time"
              size="small"
              label="Start"
              value={availability.startTime || '09:00'}
              onChange={handleStart}
              disabled={isClosed}
              InputLabelProps={{ shrink: true }}
              inputProps={minTime || maxTime ? { min: minTime, max: maxTime } : {}}
            />
          )}

          {isTimedMode && (
            <TextField
              type="time"
              size="small"
              label="End"
              value={availability.endTime || '17:00'}
              onChange={handleEnd}
              disabled={isClosed}
              InputLabelProps={{ shrink: true }}
              inputProps={minTime || maxTime ? { min: minTime, max: maxTime } : {}}
            />
          )}

          {isUnavailableMode && (
            <FormControl fullWidth size="small">
              <InputLabel>Reason</InputLabel>
              <Select
                value={availability.reason || ''}
                onChange={handleReason}
                label="Reason"
                disabled={isClosed}
              >
                <MenuItem value="">No reason selected</MenuItem>
                {UNAVAILABLE_REASONS.map((reasonOption) => (
                  <MenuItem key={reasonOption.value} value={reasonOption.value}>
                    {reasonOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
    );
  }

  // Initialize weekly availability for new form
  useEffect(() => {
    if (showAddForm && Object.keys(weeklyAvailability).length === 0) {
      const defaultWeekly = {};
      // try to use schedulingHours from the selected location if available
      const loc = (selectedLocation && locations && locations.length) ? locations.find(l => l.id === selectedLocation) : null;
      const scheduling = loc ? (loc.schedulingHours || {}) : {};
      DAYS_OF_WEEK.forEach(day => {
        const sh = scheduling[day.fullLabel] || scheduling[day.label] || scheduling[day.key] || null;
        const isClosed = sh ? sh.open === false : false;
        defaultWeekly[day.key] = {
          type: isClosed ? 'all_day_not_available' : 'all_day_available',
          // use scheduling hours if present
          startTime: sh && sh.start ? sh.start : '09:00',
          endTime: sh && sh.end ? sh.end : '17:00',
          reason: '',
          comments: '',
          _scheduling: sh || null
        };
      });
      setWeeklyAvailability(defaultWeekly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddForm]); // Only depend on showAddForm to avoid infinite loops

  // Load existing availability records
  useEffect(() => {
    if (!selectedLocation) return;

    let unsub = null;
    (async () => {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) return setAvailabilityRecords([]);

      const q = query(
        collection(db, `tenants/${tenantId}/availability`),
        where("locationId", "==", selectedLocation)
      );

      unsub = onSnapshot(q, (snapshot) => {
        const records = [];
        snapshot.forEach((doc) => {
          records.push({ id: doc.id, ...doc.data() });
        });
        setAvailabilityRecords(records);
      });
    })();

    return () => { if (unsub) unsub(); };
  }, [selectedLocation]);

  // Check for existing recurring availability when employee changes
  useEffect(() => {
    if (selectedEmployee && availabilityType === 'temporary') {
      const recurringAvail = availabilityRecords.find(a => 
        a.employeeId === selectedEmployee && 
        a.type === 'recurring' && 
        a.locationId === selectedLocation
      );
      setExistingRecurring(recurringAvail);
    } else {
      setExistingRecurring(null);
    }
  }, [selectedEmployee, availabilityType, availabilityRecords, selectedLocation]);

  // Get filtered employees for the selected location
  const filteredEmployees = useMemo(() => {
    if (!selectedLocation) return employees;
    return employees.filter(e => {
      if (e.locationId === selectedLocation) return true;
      const empAssignSlice = (assignmentsMap && assignmentsMap[e.id]) || null;
      if (empAssignSlice && empAssignSlice.departments) return true;
      if (e.assignments && e.assignments[selectedLocation]) return true;
      return false;
    });
  }, [employees, selectedLocation, assignmentsMap]);

  // Compute available counts per weekday (Monday..Sunday) based on records and employees.
  const { availabilityChartData, maxAvailable } = useMemo(() => {
    // Initialize counts map keyed by day.key
    const counts = {};
    DAYS_OF_WEEK.forEach(d => { counts[d.key] = 0; });

    // Helper to determine if a single weeklySchedule entry marks availability
    const isEntryAvailable = (entry) => {
      if (!entry || !entry.type) return true; // default available
      return entry.type === 'all_day_available' || entry.type === 'available';
    };

    // Helper to check if a temporary record is active on a given date
    const isTempActiveOnDate = (rec, date) => {
      if (!rec.startDate || !rec.endDate) return false;
      try {
        const s = rec.startDate.toDate ? rec.startDate.toDate() : new Date(rec.startDate);
        const e = rec.endDate.toDate ? rec.endDate.toDate() : new Date(rec.endDate);
        // normalize time portion
        const ds = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        const de = new Date(e.getFullYear(), e.getMonth(), e.getDate());
        const dt = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return dt >= ds && dt <= de;
      } catch (err) {
        return false;
      }
    };

    // compute dates for current week (Monday -> Sunday)
    const today = new Date();
    const day = today.getDay(); // 0 (Sun) - 6 (Sat)
    // get Monday
    const monday = new Date(today);
    const diffToMonday = ((day + 6) % 7); // number of days since Monday
    monday.setDate(today.getDate() - diffToMonday);
    const weekDates = {};
    // map day.key (1..6,0) -> Date
    DAYS_OF_WEEK.forEach((d, idx) => {
      const date = new Date(monday);
      // Monday is index 0 in DAYS_OF_WEEK array, so add idx days
      date.setDate(monday.getDate() + idx);
      weekDates[d.key] = date;
    });

    // For each employee, determine availability per day
    filteredEmployees.forEach(emp => {
      // default availability true for each day
      const avail = {};
      DAYS_OF_WEEK.forEach(d => { avail[d.key] = true; });

      // all records for this employee at the current location
      const recs = availabilityRecords.filter(r => r.employeeId === emp.id && r.locationId === selectedLocation);

      // temporary records override recurring; apply temporaries first
      recs.filter(r => r.type === 'temporary').forEach(r => {
        DAYS_OF_WEEK.forEach(d => {
          const dt = weekDates[d.key];
          if (isTempActiveOnDate(r, dt) && r.weeklySchedule && r.weeklySchedule[d.key]) {
            avail[d.key] = isEntryAvailable(r.weeklySchedule[d.key]);
          }
        });
      });

      // then apply recurring records for any days not explicitly overridden
      recs.filter(r => r.type === 'recurring').forEach(r => {
        DAYS_OF_WEEK.forEach(d => {
          if (r.weeklySchedule && r.weeklySchedule[d.key]) {
            // only set if not previously set by a temp record
            avail[d.key] = isEntryAvailable(r.weeklySchedule[d.key]);
          }
        });
      });

      // count
      DAYS_OF_WEEK.forEach(d => {
        if (avail[d.key]) counts[d.key] = (counts[d.key] || 0) + 1;
      });
    });

    const max = Math.max(1, filteredEmployees.length);
    const data = DAYS_OF_WEEK.map(d => ({ day: d.label, key: d.key, available: counts[d.key] || 0 }));
    return { availabilityChartData: data, maxAvailable: max };
  }, [availabilityRecords, filteredEmployees, selectedLocation, DAYS_OF_WEEK]);

  // Compute top 5 most blocked days (employees unavailable) using availabilityChartData
  const mostCommonDaysOff = useMemo(() => {
    const total = filteredEmployees.length || 0;
    const list = DAYS_OF_WEEK.map(d => {
      const availableEntry = availabilityChartData.find(a => a.key === d.key);
      const available = availableEntry ? availableEntry.available : 0;
      const blocked = Math.max(0, total - available);
      return { key: d.key, day: d.label, blocked };
    });
    return list.sort((a, b) => b.blocked - a.blocked).slice(0, 5);
  }, [availabilityChartData, filteredEmployees, DAYS_OF_WEEK]);

  // Compute top 5 most flexible employees (most available days this week)
  const mostFlexibleEmployees = useMemo(() => {
    // Helper to parse HH:MM -> minutes
    const parseMinutes = (t) => {
      if (!t) return null;
      const m = t.split(':');
      if (m.length !== 2) return null;
      const hh = parseInt(m[0], 10);
      const mm = parseInt(m[1], 10);
      if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
      return hh * 60 + mm;
    };

    // compute scheduling minutes per day from location settings
    const loc = (selectedLocation && locations && locations.length) ? locations.find(l => l.id === selectedLocation) : null;
    const scheduling = loc ? (loc.schedulingHours || {}) : {};

    const daySchedulingMinutes = {};
    DAYS_OF_WEEK.forEach(d => {
      const sh = scheduling[d.fullLabel] || scheduling[d.label] || scheduling[d.key] || null;
      if (!sh || sh.open === false) {
        daySchedulingMinutes[d.key] = 0;
      } else {
        const sMin = parseMinutes(sh.start || '00:00');
        const eMin = parseMinutes(sh.end || '00:00');
        if (sMin === null || eMin === null) {
          daySchedulingMinutes[d.key] = 0;
        } else {
          // handle overnight end <= start
          const duration = (eMin > sMin) ? (eMin - sMin) : (24 * 60 - sMin + eMin);
          daySchedulingMinutes[d.key] = Math.max(0, duration);
        }
      }
    });

    const totalSchedulingMinutes = Object.values(daySchedulingMinutes).reduce((a, b) => a + b, 0);

    // compute current week dates
    const today = new Date();
    const dayIndex = today.getDay(); // 0 (Sun) - 6 (Sat)
    const monday = new Date(today);
    const diffToMonday = ((dayIndex + 6) % 7);
    monday.setDate(today.getDate() - diffToMonday);
    const weekDates = {};
    DAYS_OF_WEEK.forEach((d, idx) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + idx);
      weekDates[d.key] = date;
    });

    const results = filteredEmployees.map(emp => {
      const recs = availabilityRecords.filter(r => r.employeeId === emp.id && r.locationId === selectedLocation);

      let minutesAvailable = 0;

      DAYS_OF_WEEK.forEach(d => {
        const schedMins = daySchedulingMinutes[d.key] || 0;
        if (schedMins === 0) return; // closed or zero scheduling

        // determine effective entry for this day (temporary overrides recurring)
        let entry = null;

        // temporaries active for this date
        const date = weekDates[d.key];
        recs.filter(r => r.type === 'temporary').forEach(r => {
          if (!r.startDate || !r.endDate || !r.weeklySchedule) return;
          try {
            const s = r.startDate.toDate ? r.startDate.toDate() : new Date(r.startDate);
            const e = r.endDate.toDate ? r.endDate.toDate() : new Date(r.endDate);
            const ds = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const de = new Date(e.getFullYear(), e.getMonth(), e.getDate());
            const dtNorm = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            if (dtNorm >= ds && dtNorm <= de) {
              const ent = r.weeklySchedule && r.weeklySchedule[d.key];
              if (ent) entry = ent;
            }
          } catch (err) { /* ignore */ }
        });

        // if no temporary, look for recurring
        if (!entry) {
          recs.filter(r => r.type === 'recurring').forEach(r => {
            if (r.weeklySchedule && r.weeklySchedule[d.key]) {
              entry = r.weeklySchedule[d.key];
            }
          });
        }

        // compute minutes available for this entry
        if (!entry || entry.type === 'all_day_available') {
          minutesAvailable += schedMins;
        } else if (entry.type === 'all_day_not_available') {
          minutesAvailable += 0;
        } else if (entry.type === 'available') {
          // compute overlap between scheduling window and entry start/end
          const sh = scheduling[d.fullLabel] || scheduling[d.label] || scheduling[d.key] || null;
          const schedStart = parseMinutes(sh && sh.start ? sh.start : null);
          const schedEnd = parseMinutes(sh && sh.end ? sh.end : null);
          const entStart = parseMinutes(entry.startTime || entry.start || null) ;
          const entEnd = parseMinutes(entry.endTime || entry.end || null) ;
          if (schedStart === null || schedEnd === null || entStart === null || entEnd === null) {
            // fallback: if parse fails, assume full scheduling
            minutesAvailable += schedMins;
          } else {
            // normalize ranges to minutes since monday start not needed; handle overnight by converting both to ranges 0..1440 and possibly extending
            const normalizeRange = (s,e) => {
              if (e > s) return [[s,e]];
              // overnight
              return [[s, 24*60], [0, e]];
            };
            const schedRanges = normalizeRange(schedStart, schedEnd);
            const entRanges = normalizeRange(entStart, entEnd);
            let overlap = 0;
            schedRanges.forEach(sr => {
              entRanges.forEach(er => {
                const lo = Math.max(sr[0], er[0]);
                const hi = Math.min(sr[1], er[1]);
                if (hi > lo) overlap += (hi - lo);
              });
            });
            minutesAvailable += Math.max(0, Math.min(overlap, schedMins));
          }
        } else if (entry.type === 'not_available') {
          minutesAvailable += 0;
        } else {
          // unknown type -> assume available
          minutesAvailable += schedMins;
        }
      });

      const percent = totalSchedulingMinutes > 0 ? (minutesAvailable / totalSchedulingMinutes) * 100 : (DAYS_OF_WEEK.length ? (minutesAvailable / (DAYS_OF_WEEK.length * 24 * 60)) * 100 : 0);
      return {
        id: emp.id,
        name: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
        minutesAvailable,
        percentAvailable: Math.round(percent)
      };
    });

    return results.sort((a, b) => b.percentAvailable - a.percentAvailable || a.name.localeCompare(b.name)).slice(0, 5);
  }, [filteredEmployees, availabilityRecords, selectedLocation]);

  // Get selected employee object
  const selectedEmployeeObj = useMemo(() => {
    return filteredEmployees.find(e => e.id === selectedEmployee);
  }, [filteredEmployees, selectedEmployee]);

  const searchedAvailabilityRecords = useMemo(() => {
    const queryText = employeeSearch.trim().toLowerCase();
    if (!queryText) return availabilityRecords;

    return availabilityRecords.filter((record) => {
      const employee = filteredEmployees.find((entry) => entry.id === record.employeeId)
        || employees.find((entry) => entry.id === record.employeeId);
      return formatEmployeeName(employee).toLowerCase().includes(queryText);
    });
  }, [availabilityRecords, filteredEmployees, employees, employeeSearch]);

  const toggleRecordExpanded = (recordId) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [recordId]: !prev[recordId],
    }));
  };

  const handleWeeklyAvailabilityChange = (dayKey, newAvailability) => {
    setWeeklyAvailability(prev => ({
      ...prev,
      [dayKey]: newAvailability
    }));
  };

  const handleSubmit = async () => {
    if (!selectedEmployee || !selectedLocation || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const baseData = {
        employeeId: selectedEmployee,
        locationId: selectedLocation,
        type: availabilityType,
        updatedAt: new Date(),
      };

      // Only set createdAt for new records
      if (!editingRecord) {
        baseData.createdAt = new Date();
      }

      if (availabilityType === 'temporary') {
        baseData.startDate = new Date(startDate);
        baseData.endDate = new Date(endDate);
      }

      // Save weekly availability as a single document with all days
      const availabilityDoc = {
        ...baseData,
        weeklySchedule: weeklyAvailability
      };

      if (editingRecord) {
  // Update existing record
  const tenantId = await tryResolveTenantId();
  if (!tenantId) throw new Error('tenantId not resolved');
  await updateDoc(doc(db, `tenants/${tenantId}/availability`, editingRecord.id), availabilityDoc);
        setSnackbar({
          open: true,
          message: `${availabilityType === 'recurring' ? 'Recurring' : 'Temporary'} availability updated successfully!`,
          severity: "success"
        });
      } else {
  // Create new record
  const tenantId = await tryResolveTenantId();
  if (!tenantId) throw new Error('tenantId not resolved');
  await addDoc(collection(db, `tenants/${tenantId}/availability`), availabilityDoc);
        setSnackbar({
          open: true,
          message: `${availabilityType === 'recurring' ? 'Recurring' : 'Temporary'} availability saved successfully!`,
          severity: "success"
        });
      }

      // Reset form
      setShowAddForm(false);
      setEditingRecord(null);
      setSelectedEmployee("");
      setAvailabilityType("recurring");
      setStartDate(getTodayString());
      setEndDate("");
      setWeeklyAvailability({});

    } catch (error) {
      console.error("Error saving availability:", error);
      setSnackbar({
        open: true,
        message: "Error saving availability. Please try again.",
        severity: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setSelectedEmployee("");
    setAvailabilityType("recurring");
    setStartDate(getTodayString());
    setEndDate("");
    setWeeklyAvailability({});
  };

  const handleEditAvailability = (record) => {
    // Pre-fill the form with existing data
    setEditingRecord(record); // Track which record is being edited
    setSelectedEmployee(record.employeeId);
    setAvailabilityType(record.type);
    if (record.type === 'temporary') {
      setStartDate(new Date(record.startDate.toDate()).toISOString().split('T')[0]);
      setEndDate(new Date(record.endDate.toDate()).toISOString().split('T')[0]);
    }
    if (record.weeklySchedule) {
      // attach scheduling hours from selected location if available
      const loc = (selectedLocation && locations && locations.length) ? locations.find(l => l.id === selectedLocation) : null;
      const scheduling = loc ? (loc.schedulingHours || {}) : {};
      const merged = {};
      DAYS_OF_WEEK.forEach(d => {
        const entry = record.weeklySchedule[d.key] || record.weeklySchedule[d.label] || record.weeklySchedule[d.fullLabel] || null;
        const sh = scheduling[d.fullLabel] || scheduling[d.label] || scheduling[d.key] || null;
        merged[d.key] = { ...(entry || {}), _scheduling: sh || null };
      });
      setWeeklyAvailability(merged);
    }
    setShowAddForm(true);
    setSnackbar({
      open: true,
      message: "Editing existing availability record. Make your changes and save.",
      severity: "info"
    });
  };

  const handleDeleteAvailability = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this availability record?")) {
      return;
    }

    try {
  const tenantId = await tryResolveTenantId();
  if (!tenantId) throw new Error('tenantId not resolved');
  await deleteDoc(doc(db, `tenants/${tenantId}/availability`, recordId));
      setSnackbar({
        open: true,
        message: "Availability record deleted successfully!",
        severity: "success"
      });
    } catch (error) {
      console.error("Error deleting availability:", error);
      setSnackbar({
        open: true,
        message: "Error deleting availability record. Please try again.",
        severity: "error"
      });
    }
  };

  if (showAddForm) {
    return (
      <Container maxWidth={false} disableGutters sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
        <Box
          sx={{
            ...SURFACE_STYLES,
            px: { xs: 2, md: 3.5 },
            py: { xs: 2.25, md: 3 },
            mb: 2.5,
            background: 'linear-gradient(130deg, rgba(37,99,235,0.96) 0%, rgba(59,130,246,0.92) 48%, rgba(13,148,136,0.82) 100%)',
            color: 'common.white',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 'auto -8% -42% auto',
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 68%)',
              pointerEvents: 'none',
            }}
          />
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{
              mb: 1.5,
              color: 'common.white',
              borderColor: 'rgba(255,255,255,0.24)',
              bgcolor: 'rgba(255,255,255,0.08)',
              borderRadius: 999,
              px: 1.35,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
            }}
          >
            Availability requests
          </Button>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, letterSpacing: 1.6, opacity: 0.78 }}>
            AVAILABILITY SETUP
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: { xs: '2rem', md: '2.8rem' }, lineHeight: 1.05, fontWeight: 900 }}>
            {editingRecord ? 'Edit availability' : 'Add availability'}
          </Typography>
          <Typography sx={{ mt: 1, maxWidth: 760, fontSize: { xs: '1rem', md: '1.12rem' }, lineHeight: 1.7, opacity: 0.9 }}>
            Set recurring or temporary availability with a clearer day-by-day editor built for quick staffing updates.
          </Typography>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.2,
            }}
          >
            {[
              {
                label: 'Selected Employee',
                value: selectedEmployeeObj ? formatEmployeeName(selectedEmployeeObj) : 'Choose employee',
              },
              {
                label: 'Location',
                value: locations.find((location) => location.id === selectedLocation)?.name || 'Choose location',
              },
              {
                label: 'Availability Type',
                value: availabilityType === 'temporary' ? 'Temporary override' : 'Recurring pattern',
              },
              {
                label: 'Days To Configure',
                value: '7 day schedule',
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 1.35,
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.18)',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: 1.1, opacity: 0.78 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: '1rem' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {selectedEmployeeObj && existingRecurring && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
                mb: 0.25,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.92)',
              }}
            >
              {selectedEmployeeObj.name} has pre-approved recurring availability. 
              If you make changes below, they will only override availability for the selected week 
              and not the other weeks.
            </Alert>
          )}
        </Box>

        <Paper sx={{ ...SURFACE_STYLES, p: { xs: 1.75, md: 2.2 }, mb: 2.2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.3rem' }}>
              Setup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Choose the employee, the location this availability applies to, and whether this is a recurring pattern or a temporary override.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {/* Employee Selection */}
            <Grid item xs={12} md={6} sx={{ minWidth: 250 }}>
              <FormControl fullWidth required size="small">
                <InputLabel shrink>Employee</InputLabel>
                <Select
                  value={selectedEmployee}
                  label="Employee"
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  inputProps={{ 'aria-label': 'Employee' }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {filteredEmployees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.name || `${employee.firstName} ${employee.lastName}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Location Selection */}
            <Grid item xs={12} md={6}>
              <LocationSelector
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                locations={locations}
                required
              />
            </Grid>

            {/* Availability Type */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                  Type
                </FormLabel>
                <RadioGroup
                  row
                  value={availabilityType}
                  onChange={(e) => setAvailabilityType(e.target.value)}
                >
                  {AVAILABILITY_TYPES.map((type) => (
                    <FormControlLabel
                      key={type.value}
                      value={type.value}
                      control={<Radio />}
                      label={type.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Effective Dates for Temporary */}
            {availabilityType === 'temporary' && (
              <Grid item xs={12}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', mb: 1.1 }}>
                  Effective dates
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} alignItems={{ md: 'center' }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                  />
                  <Typography sx={{ display: { xs: 'none', md: 'block' }, color: 'text.secondary' }}>→</Typography>
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Weekly Availability */}
        <Paper sx={{ ...SURFACE_STYLES, p: { xs: 1.75, md: 2.2 } }}>
          <Box sx={{ mb: 1.6 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.3rem' }}>
              Weekly Availability
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Configure each day as open, blocked, or limited to specific hours. Time controls only appear when a partial-day window is needed.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.1,
            }}
          >
            {DAYS_OF_WEEK.map((day) => (
              <DayAvailabilityRow
                key={day.key}
                day={day}
                availability={weeklyAvailability[day.key] || {
                  type: 'all_day_available',
                  startTime: '09:00',
                  endTime: '17:00',
                  reason: '',
                  comments: ''
                }}
                onChange={handleWeeklyAvailabilityChange}
              />
            ))}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: '1px solid rgba(148,163,184,0.18)',
              display: 'flex',
              gap: 1.2,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <Button onClick={handleCancel} sx={{ borderRadius: 999, fontWeight: 700 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selectedEmployee || !selectedLocation || isSubmitting || 
                (availabilityType === 'temporary' && (!startDate || !endDate))}
              startIcon={<SaveIcon />}
              sx={{ borderRadius: 999, px: 2.1, fontWeight: 800 }}
            >
              {editingRecord ? 'Save Changes' : 'Save Availability'}
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }
  // Main availability list view
  return (
    <Container maxWidth={false} disableGutters sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      <Box
        sx={{
          ...SURFACE_STYLES,
          px: { xs: 2, md: 3.5 },
          py: { xs: 2.25, md: 3 },
          mb: 2.5,
          background: 'linear-gradient(130deg, rgba(37,99,235,0.96) 0%, rgba(59,130,246,0.92) 48%, rgba(13,148,136,0.82) 100%)',
          color: 'common.white',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 'auto -10% -45% auto',
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 68%)',
            pointerEvents: 'none',
          }}
        />
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2.5}
          justifyContent="space-between"
          alignItems={{ lg: 'flex-start' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, letterSpacing: 1.6, opacity: 0.78 }}>
              STAFFING COVERAGE
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.04, fontWeight: 900 }}>
              Employee Availability
            </Typography>
            <Typography sx={{ mt: 1.1, maxWidth: 720, fontSize: { xs: '1rem', md: '1.16rem' }, lineHeight: 1.7, opacity: 0.9 }}>
              Review availability coverage, recurring patterns, and schedule blockers by location from one clean planning workspace.
            </Typography>
          </Box>
          <Stack spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { lg: 260 } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
              disabled={!selectedLocation}
              sx={{
                minHeight: 50,
                px: 2.4,
                borderRadius: 3,
                fontWeight: 800,
                bgcolor: 'rgba(255,255,255,0.96)',
                color: '#2563eb',
                boxShadow: '0 14px 28px rgba(15, 23, 42, 0.18)',
                '&:hover': { bgcolor: 'common.white' },
              }}
            >
              Add Availability
            </Button>
            <Box
              sx={{
                px: 1.5,
                py: 1.15,
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.22)',
                bgcolor: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: 1.1, opacity: 0.78 }}>
                ACTIVE LOCATION
              </Typography>
              <Typography sx={{ mt: 0.45, fontSize: '1.02rem', fontWeight: 800 }}>
                {locations.find((location) => location.id === selectedLocation)?.name || 'Select a location'}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Box
          sx={{
            mt: 2.25,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.25,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {[
            {
              label: 'Records',
              value: availabilityRecords.length,
              helper: 'Availability entries on file',
            },
            {
              label: 'Employees In Scope',
              value: filteredEmployees.length,
              helper: 'Team members at this location',
            },
            {
              label: 'Top Coverage Day',
              value: availabilityChartData.length
                ? availabilityChartData.slice().sort((a, b) => b.available - a.available)[0]?.day || 'N/A'
                : 'N/A',
              helper: availabilityChartData.length
                ? `${availabilityChartData.slice().sort((a, b) => b.available - a.available)[0]?.available || 0} available`
                : 'No data yet',
            },
            {
              label: 'Most Blocked Day',
              value: mostCommonDaysOff[0]?.day || 'N/A',
              helper: mostCommonDaysOff[0] ? `${mostCommonDaysOff[0].blocked} unavailable` : 'No blockers recorded',
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                p: 1.45,
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.2)',
                bgcolor: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: 1.1, opacity: 0.78 }}>
                {item.label}
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: { xs: '1.2rem', md: '1.55rem' }, fontWeight: 900 }}>
                {item.value}
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: '0.88rem', opacity: 0.84 }}>
                {item.helper}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Paper
        sx={{
          ...SURFACE_STYLES,
          p: { xs: 1.5, md: 2 },
          mb: 2.5,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ lg: 'flex-end' }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.02rem' }}>
              Location Focus
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Switch locations to review coverage, flexibility, common time-off patterns, and search employee records by name.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <Box sx={{ width: { xs: '100%', lg: 340 } }}>
              <LocationSelector
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                locations={locations}
                required
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Search Employee"
              placeholder="Search by employee name"
              value={employeeSearch}
              onChange={(event) => setEmployeeSearch(event.target.value)}
              sx={{ width: { xs: '100%', lg: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: employeeSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setEmployeeSearch("")} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {selectedLocation && (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.45fr) minmax(320px, 0.75fr) minmax(300px, 0.7fr)' },
              gap: 2,
              alignItems: 'stretch',
              mb: 2.5,
            }}
          >
            <Paper sx={{ ...SURFACE_STYLES, p: { xs: 1.75, md: 2.2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.42rem' }}>
                    Availability by day
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    Quick view of how many employees can work each day this week.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1.3,
                    py: 0.85,
                    borderRadius: 999,
                    bgcolor: 'rgba(34,197,94,0.1)',
                    color: '#15803d',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {filteredEmployees.length} employees
                </Box>
              </Stack>
              <Box sx={{ width: '100%', height: 320, mt: 1.25 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={availabilityChartData}
                    margin={{ top: 28, right: 10, left: 0, bottom: 18 }}
                    barCategoryGap="28%"
                    barGap={8}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148, 163, 184, 0.3)" />
                    <XAxis dataKey="day" type="category" interval={0} tick={{ fill: '#475569', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis type="number" domain={[0, filteredEmployees.length]} allowDecimals={false} tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      cursor={{ fill: 'rgba(59,130,246,0.06)' }}
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid rgba(148,163,184,0.25)',
                        boxShadow: '0 14px 32px rgba(15,23,42,0.14)',
                      }}
                    />
                    <Bar dataKey="available" barSize={38} radius={[10, 10, 4, 4]}>
                      <LabelList dataKey="available" position="top" style={{ fill: '#0f172a', fontWeight: 700 }} />
                      {availabilityChartData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={valueToColor(entry.available, maxAvailable)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            <Paper sx={{ ...SURFACE_STYLES, p: { xs: 1.75, md: 2.2 } }}>
              <Typography sx={{ fontWeight: 900, fontSize: '1.42rem' }}>
                Most Flexible Employees
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Team members with the widest availability against scheduled store hours.
              </Typography>
              <Stack spacing={1.15} sx={{ mt: 1.8 }}>
                {mostFlexibleEmployees.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No flexibility data available yet.
                  </Typography>
                ) : (
                  mostFlexibleEmployees.map((employee, index) => (
                    <Box
                      key={employee.id}
                      sx={{
                        p: 1.25,
                        borderRadius: 3,
                        border: '1px solid rgba(59,130,246,0.16)',
                        bgcolor: index === 0 ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.72)',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800 }}>
                            {employee.name || 'Unknown'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                            {Math.round((employee.minutesAvailable || 0) / 60)} available hrs this week
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            minWidth: 64,
                            px: 1.1,
                            py: 0.7,
                            borderRadius: 999,
                            textAlign: 'center',
                            bgcolor: 'rgba(37,99,235,0.1)',
                            color: '#1d4ed8',
                            fontWeight: 900,
                          }}
                        >
                          {employee.percentAvailable}%
                        </Box>
                      </Stack>
                    </Box>
                  ))
                )}
              </Stack>
            </Paper>

            <Paper sx={{ ...SURFACE_STYLES, p: { xs: 1.75, md: 2.2 } }}>
              <Typography sx={{ fontWeight: 900, fontSize: '1.42rem' }}>
                Most Common Days Off
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Days with the highest concentration of unavailable employees.
              </Typography>
              <Stack spacing={1.1} sx={{ mt: 1.8 }}>
                {(() => {
                  const maxBlocked = Math.max(1, ...(mostCommonDaysOff.map((entry) => entry.blocked)));
                  return mostCommonDaysOff.map((day, index) => {
                    const badgeColor = valueToColor(maxBlocked - day.blocked, maxBlocked);
                    return (
                      <Box
                        key={day.key}
                        sx={{
                          p: 1.2,
                          borderRadius: 3,
                          border: '1px solid rgba(148,163,184,0.18)',
                          bgcolor: 'rgba(255,255,255,0.72)',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800 }}>
                              {index + 1}. {day.day}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                              {day.blocked} employees unavailable
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: `${badgeColor}22`,
                              border: `1px solid ${badgeColor}33`,
                              flexShrink: 0,
                            }}
                          >
                            <Typography sx={{ fontWeight: 900, color: badgeColor }}>
                              {day.blocked}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  });
                })()}
              </Stack>
            </Paper>
          </Box>

          <Paper sx={{ ...SURFACE_STYLES, p: { xs: 1.75, md: 2.2 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
              spacing={1.5}
              sx={{ mb: 1.8 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '1.42rem' }}>
                  Availability Records
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  Review recurring and temporary availability by employee, with each day grouped into fast-scan schedule cards.
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.35,
                  py: 0.85,
                  borderRadius: 999,
                  bgcolor: 'rgba(37,99,235,0.08)',
                  color: '#2563eb',
                  fontWeight: 800,
                  fontSize: '0.88rem',
                }}
              >
                {employeeSearch.trim()
                  ? `${searchedAvailabilityRecords.length} of ${availabilityRecords.length} records`
                  : `${availabilityRecords.length} records`}
              </Box>
            </Stack>

            {availabilityRecords.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px dashed rgba(148,163,184,0.35)',
                  bgcolor: 'rgba(255,255,255,0.72)',
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>
                  No availability records found for this location.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
                  Add the first availability record to start tracking weekly coverage patterns.
                </Typography>
              </Box>
            ) : searchedAvailabilityRecords.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px dashed rgba(148,163,184,0.35)',
                  bgcolor: 'rgba(255,255,255,0.72)',
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>
                  No employee matches “{employeeSearch}”.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
                  Try a different name or clear the search to view all availability records for this location.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.6}>
                {searchedAvailabilityRecords.map((record) => {
                  const employee = filteredEmployees.find((entry) => entry.id === record.employeeId);
                  const employeeName = formatEmployeeName(employee);
                  const weeklyEntries = DAYS_OF_WEEK
                    .map((day) => ({ day, entry: record.weeklySchedule?.[day.key] }))
                    .filter(({ entry }) => !!entry);
                  const availableCount = weeklyEntries.filter(({ entry }) =>
                    entry.type === 'all_day_available' || entry.type === 'available'
                  ).length;
                  const blockedCount = weeklyEntries.filter(({ entry }) =>
                    entry.type === 'all_day_not_available' || entry.type === 'not_available'
                  ).length;
                  const isExpanded = !!expandedRecords[record.id];

                  return (
                    <Card
                      key={record.id}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.2)',
                        boxShadow: '0 10px 24px rgba(15,23,42,0.05)',
                        bgcolor: 'rgba(255,255,255,0.86)',
                      }}
                    >
                      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                        <Stack
                          direction={{ xs: 'column', lg: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ lg: 'flex-start' }}
                          spacing={1.5}
                        >
                          <Box
                            sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
                            onClick={() => toggleRecordExpanded(record.id)}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                              <Typography sx={{ fontWeight: 900, fontSize: '1.18rem' }}>
                                {employeeName}
                              </Typography>
                              <Box
                                sx={{
                                  px: 1.05,
                                  py: 0.45,
                                  borderRadius: 999,
                                  bgcolor: record.type === 'recurring' ? 'rgba(37,99,235,0.08)' : 'rgba(217,119,6,0.1)',
                                  color: record.type === 'recurring' ? '#2563eb' : '#b45309',
                                  fontWeight: 800,
                                  fontSize: '0.8rem',
                                }}
                              >
                                {record.type === 'recurring' ? 'Recurring' : 'Temporary'}
                              </Box>
                            </Stack>

                            {record.type === 'temporary' && record.startDate && record.endDate ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
                                Active {new Date(record.startDate.toDate()).toLocaleDateString()} - {new Date(record.endDate.toDate()).toLocaleDateString()}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
                                Ongoing weekly availability template
                              </Typography>
                            )}

                            <Stack direction="row" spacing={0.9} sx={{ mt: 1.1, flexWrap: 'wrap', rowGap: 0.75 }}>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.45,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(22,163,74,0.1)',
                                  color: '#15803d',
                                  fontWeight: 800,
                                  fontSize: '0.76rem',
                                }}
                              >
                                {availableCount} open days
                              </Box>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.45,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(220,38,38,0.1)',
                                  color: '#dc2626',
                                  fontWeight: 800,
                                  fontSize: '0.76rem',
                                }}
                              >
                                {blockedCount} blocked days
                              </Box>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.45,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(37,99,235,0.08)',
                                  color: '#2563eb',
                                  fontWeight: 800,
                                  fontSize: '0.76rem',
                                }}
                              >
                                {isExpanded ? 'Expanded' : 'Collapsed'}
                              </Box>
                            </Stack>
                          </Box>

                          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
                            <IconButton
                              onClick={() => toggleRecordExpanded(record.id)}
                              sx={{
                                border: '1px solid rgba(148,163,184,0.24)',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 180ms ease',
                              }}
                              aria-label={isExpanded ? 'Collapse availability record' : 'Expand availability record'}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditAvailability(record)}
                              sx={{ borderRadius: 999, fontWeight: 800 }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteAvailability(record.id)}
                              sx={{ borderRadius: 999, fontWeight: 800 }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>

                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          {record.weeklySchedule && (
                            <Box sx={{ mt: 1.7 }}>
                              <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, letterSpacing: 0.9, color: 'text.secondary' }}>
                                WEEKLY SCHEDULE
                              </Typography>
                              <Box
                                sx={{
                                  mt: 1.05,
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                                  gap: 1,
                                }}
                              >
                                {DAYS_OF_WEEK.map((day) => {
                                  const dayAvail = record.weeklySchedule[day.key];
                                  if (!dayAvail) return null;
                                  const scheduleEntry = formatScheduleEntry(dayAvail, UNAVAILABLE_REASONS);
                                  const toneStyles = scheduleEntry.tone === 'negative'
                                    ? {
                                        bg: 'rgba(254,242,242,0.92)',
                                        border: 'rgba(248,113,113,0.32)',
                                        chipBg: 'rgba(220,38,38,0.12)',
                                        chipColor: '#dc2626',
                                      }
                                    : scheduleEntry.tone === 'positive'
                                      ? {
                                          bg: 'rgba(239,253,244,0.92)',
                                          border: 'rgba(74,222,128,0.32)',
                                          chipBg: 'rgba(22,163,74,0.12)',
                                          chipColor: '#15803d',
                                        }
                                      : {
                                          bg: 'rgba(248,250,252,0.92)',
                                          border: 'rgba(148,163,184,0.28)',
                                          chipBg: 'rgba(100,116,139,0.12)',
                                          chipColor: '#475569',
                                        };

                                  return (
                                    <Box
                                      key={day.key}
                                      sx={{
                                        p: 1.1,
                                        borderRadius: 3,
                                        border: `1px solid ${toneStyles.border}`,
                                        bgcolor: toneStyles.bg,
                                      }}
                                    >
                                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                        <Stack direction="row" spacing={0.9} alignItems="center">
                                          <Box
                                            sx={{
                                              width: 10,
                                              height: 10,
                                              borderRadius: '50%',
                                              backgroundColor: day.color,
                                              flexShrink: 0,
                                            }}
                                          />
                                          <Typography sx={{ fontWeight: 800 }}>
                                            {day.label}
                                          </Typography>
                                        </Stack>
                                        <Box
                                          sx={{
                                            px: 0.85,
                                            py: 0.35,
                                            borderRadius: 999,
                                            bgcolor: toneStyles.chipBg,
                                            color: toneStyles.chipColor,
                                            fontWeight: 800,
                                            fontSize: '0.72rem',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {scheduleEntry.tone === 'negative' ? 'Blocked' : scheduleEntry.tone === 'positive' ? 'Open' : 'Unspecified'}
                                        </Box>
                                      </Stack>

                                      <Typography sx={{ mt: 0.85, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.35 }}>
                                        {scheduleEntry.summary}
                                      </Typography>

                                      {(scheduleEntry.detail || dayAvail.comments) && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.45 }}>
                                          {dayAvail.comments && scheduleEntry.detail && scheduleEntry.detail !== dayAvail.comments
                                            ? `${scheduleEntry.detail} • ${dayAvail.comments}`
                                            : (dayAvail.comments || scheduleEntry.detail)}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}

                          {record.createdAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                              Created {new Date(record.createdAt.toDate()).toLocaleDateString()} at {new Date(record.createdAt.toDate()).toLocaleTimeString()}
                            </Typography>
                          )}
                        </Collapse>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
